/**
 * Slim API route registry for the buy funnel. Only the routes the checkout actually hits.
 * If you find yourself dereferencing something not registered here, register it explicitly —
 * a missing helper throws TypeError at runtime, which is worse than a compile error.
 */
export default defineNuxtPlugin(() => {
    const routes = {
        me: {
            getMe: () => `/me`,
            // GET returns the member's own license gifts, read for who still depends on their plan
            // before a change to Essentials ends that protection.
            licenseGifts: () => `/me/license-gifts`,
            financial: {
                // Finalizes a 3DS-authenticated PaymentIntent + flips the invoice to PAID
                // server-side. /me variant looks up the authed person.
                confirmPayment: () => `/me/financial/confirm-payment`,
            },
        },
        auth: {
            // POST — request a 6-digit email code (auto-creates a lead if the email is new).
            start: () => `/auth/start`,
            // POST — verify the code; returns Passport token + refresh_token. Needs credentials.
            verify: () => `/auth/verify`,
            // PATCH — set first/last name after login (only fills when currently empty).
            profile: () => `/auth/profile`,
        },
        licenses: {
            // GET — public consumer pricing (no auth) so logged-out buyers can see prices.
            standardCostsPublic: () => `/licenses/standard-costs`,
        },
        billing: {
            // GET — public Stripe publishable key for this environment (no auth). Paired
            // server-side with the secret key the backend charges, so the key the browser
            // loads Stripe with always matches the backend's account/mode.
            stripeConfig: () => `/billing/stripe-config`,
        },
        people: {
            licenses: {
                // GET returns the buyer's owned-licenses collection (needed post-purchase to
                // find the freshly created license for gift-mode auto-assign).
                get_licenses: (personId: number) => `/people/${personId}/licenses`,
                // POST creates the purchase. Same path as get_licenses.
                purchase_licenses: (personId: number) => `/people/${personId}/licenses`,
                // PATCH to assign / unassign / set renew flag on a specific license.
                update_license: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}`,
                // PATCH to change plan/term on an existing license (the modify/upgrade flow).
                change_license: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}/change`,
                // POST to get the prorated breakdown for an upgrade (License Credit, Annual
                // Discount, Multi-license Discount, Due Today, etc.).
                calculate_upgrade_price: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}/calculate-upgrade-price`,
            },
            billing: {
                creditBalance: (personId: number) => `/people/${personId}/financial/credit-balance`,
                giftCertificates: {
                    apply: (personId: number) => `/people/${personId}/financial/gift-certificates/apply`,
                },
            },
        },
        companies: {
            // GET — the company the business order is for: name, slug and the
            // negotiated license pricing the tier cards are priced from. The `/apps`
            // payload rather than the bare company record, which carries neither the
            // pricing nor a membership check: this one 401s unless the caller is an
            // employee of the company (or a partner over it, or PB staff).
            show: (companyId: number) => `/companies/${companyId}/apps`,
            orders: {
                // POST — side-effect-free preview: line subtotals, order total, and the
                // company's live credit state. Called on every quantity change.
                quote: (companyId: number) => `/companies/${companyId}/orders/quote`,
                // POST — commit. At-most-once via the order token.
                place: (companyId: number) => `/companies/${companyId}/orders`,
                // GET — what became of that order. The server's answer, so a reload
                // does not have to reconstruct it from what this tab remembered.
                resume: (companyId: number, orderToken: string) =>
                    `/companies/${companyId}/orders/${encodeURIComponent(orderToken)}`,
            },
            // The company's saved cards — same collection shape as a person's.
            paymentMethods: (companyId: number) => `/companies/${companyId}/financial/payment-methods`,
            paymentMethodSetupIntent: (companyId: number) =>
                `/companies/${companyId}/financial/payment-methods/setup-intent`,
        },
        public: {
            proposals: {
                // GET — the proposal a link opens, keyed on the link itself (a 64-hex key,
                // or a 10-character display number while the compatibility window is
                // open). Optional auth: the bearer rides along when there is one, and a
                // stale one still gets the payload rather than a 401.
                show: (key: string) => `/public/proposals/${encodeURIComponent(key)}`,
                // POST — approve it, echoing the version the page read and the card chosen.
                approve: (key: string) => `/public/proposals/${encodeURIComponent(key)}/approve`,
            },
        },
    };

    return {
        provide: {
            api: { routes },
        },
    };
});
